import type { SyntheticEvent } from "react";
import { getIn } from "formik";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { LocalizationProvider } from '@mui/x-date-pickers-v.6/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers-v.6/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers-v.6/DatePicker';
import { PROOF_FILE } from "consts/ecommerce";
import { getFormInputProps } from "util/forms";
import { adultDate } from "util/support";
import { UiDateTimeFormatter } from "util/formaters";
import { TextFieldLoadable } from "ui/atoms/TextField";
import { InputableRadioGroup, OnboardingInputControl } from "ui/molecules/Input";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import { ProofFileInput, acceptRules, IdentityPlaceholderTitle } from "ui/forms/formik-elements/FileInput";
import { FormField, type OnboardingDirectorsAndShareholdersProps } from "../types";

type ShareholderProps = OnboardingDirectorsAndShareholdersProps & {
    readonly index: number;
};

const Shareholder = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    isIncomplete,
    requestedChangeFields,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    index,
    ...form
}: ShareholderProps) => {
    const getFieldName = (field: string) => (
        `${FormField.OtherPeople.FieldPrefix}[${index}].${field}`
    );

    const entity = getIn(values, `${FormField.OtherPeople.FieldPrefix}[${index}]`);

    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 2
            }}
        >
            <Typography
                variant='h6'
            >
                Upload Passport*
            </Typography>
            <Typography
                variant='caption'
            >
                Passport should be in date and within 3 months of expiry
            </Typography>
            <OnboardingInputControl
                name={`${entity.pki}.${FormField.OtherPeople.PassportFileKey}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <ProofFileInput
                        multiple={false}
                        disabled={disabled}
                        name={getFieldName(FormField.OtherPeople.PassportFileKey)}
                        file={entity[FormField.OtherPeople.PassportFileKey]}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        placeholderProps={{
                            titleSlot: (
                                <IdentityPlaceholderTitle />
                            )
                        }}
                        accept={acceptRules.identity}
                        maxSize={PROOF_FILE.maxSize}
                    />
                )}
            />
            <Typography
                variant='h6'
            >
                Upload Utility Bill*
            </Typography>
            <Typography
                variant='caption'
            >
                Dated within the previous 3 months
            </Typography>
            <OnboardingInputControl
                name={`${entity.pki}.${FormField.OtherPeople.UtilityBillFileKey}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <ProofFileInput
                        multiple={false}
                        disabled={disabled}
                        name={getFieldName(FormField.OtherPeople.UtilityBillFileKey)}
                        file={entity[FormField.OtherPeople.UtilityBillFileKey]}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        placeholderProps={{
                            titleSlot: (
                                <IdentityPlaceholderTitle />
                            )
                        }}
                        accept={acceptRules.identity}
                        maxSize={PROOF_FILE.maxSize}
                    />
                )}
            />
            <Typography
                variant='h6'
            >
                Personal Information
            </Typography>
            <OnboardingInputControl
                name={`${entity.pki}.${FormField.OtherPeople.Name}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(getFieldName(FormField.OtherPeople.Name), form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={getFieldName(FormField.OtherPeople.Name)}
                        value={entity[FormField.OtherPeople.Name]}
                        label='Full Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={`${entity.pki}.${FormField.OtherPeople.DateOfBirth}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                    >
                        <DatePicker
                            disableFuture
                            disabled={disabled}
                            maxDate={adultDate()}
                            format={UiDateTimeFormatter.Ui}
                            label='Date of Birth'
                            slotProps={{
                                textField: {
                                    ...getFormInputProps(getFieldName(FormField.OtherPeople.DateOfBirth), form),
                                    name: getFieldName(FormField.OtherPeople.DateOfBirth),
                                    size: 'small',
                                    required: true,
                                    fullWidth: true,
                                    onBlur: handleBlur
                                }
                            }}
                            value={entity[FormField.OtherPeople.DateOfBirth]}
                            onChange={value => {
                                setFieldValue(getFieldName(FormField.OtherPeople.DateOfBirth), value);
                                setTimeout(() => setFieldTouched(getFieldName(FormField.OtherPeople.DateOfBirth), true, true));
                            }}
                        />
                    </LocalizationProvider>
                )}
            />
            <OnboardingInputControl
                name={`${entity.pki}.${FormField.OtherPeople.CountryOrigin}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <CountryIso2
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        value={entity[FormField.OtherPeople.CountryOrigin] ?? ''}
                        onChange={onAutocompleteChange(getFieldName(FormField.OtherPeople.CountryOrigin))}
                        TextFieldProps={{
                            ...getFormInputProps(getFieldName(FormField.OtherPeople.CountryOrigin), form),
                            required: true,
                            name: getFieldName(FormField.OtherPeople.CountryOrigin),
                            label: 'Nationality'
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={`${entity.pki}.${FormField.OtherPeople.AddressLine1}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(getFieldName(FormField.OtherPeople.AddressLine1), form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={getFieldName(FormField.OtherPeople.AddressLine1)}
                        value={entity[FormField.OtherPeople.AddressLine1]}
                        label='Residential Address'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                variant='h6'
            >
                Contact Information
            </Typography>
            <OnboardingInputControl
                name={`${entity.pki}.${FormField.OtherPeople.Email}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(getFieldName(FormField.OtherPeople.Email), form)}
                        fullWidth
                        required
                        disabled={disabled}
                        type='email'
                        size='small'
                        name={getFieldName(FormField.OtherPeople.Email)}
                        value={entity[FormField.OtherPeople.Email]}
                        label='E-mail'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={`${entity.pki}.${FormField.OtherPeople.Phone}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(getFieldName(FormField.OtherPeople.Phone), form)}
                        fullWidth
                        required
                        disabled={disabled}
                        type='tel'
                        size='small'
                        name={getFieldName(FormField.OtherPeople.Phone)}
                        value={entity[FormField.OtherPeople.Phone]}
                        label='Phone'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                variant='h6'
            >
                What is the ownership percentage of this shareholder?
            </Typography>
            <OnboardingInputControl
                name={`${entity.pki}.${FormField.OtherPeople.SharesPercentage}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextFieldLoadable
                        {...getFormInputProps(getFieldName(FormField.OtherPeople.SharesPercentage), form)}
                        fullWidth
                        required
                        modifiers={{
                            between: [0.01, 100]
                        }}
                        InputProps={{
                            endAdornment: '%'
                        }}
                        disabled={disabled}
                        type='number'
                        size='small'
                        name={getFieldName(FormField.OtherPeople.SharesPercentage)}
                        value={entity[FormField.OtherPeople.SharesPercentage]}
                        label='Ownership Percentage (%)'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={`${entity.pki}.${FormField.IsPepCeo}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        slots={{
                            textField: <></>
                        }}
                        slotProps={{
                            typography: {
                                sx: {
                                    mt: 2
                                },
                                children: 'Is this person PEP or subject to sanctions?'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': `${getFieldName(FormField.IsPepCeo)}`,
                                name: getFieldName(FormField.IsPepCeo),
                                value: entity[FormField.IsPepCeo],
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
        </Box>
    );
};

export default Shareholder;
